import { AnalyticsContext } from '@shared/contexts/AnalyticsContext';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import {
  NavigatorFields,
  PageProperties,
  TrackProperties,
  usePageMutation,
  useTrackMutation,
  VoomaApiClient,
} from '@shared/generated/graphql';
import { useCurrentClientVersion } from '@shared/utils/version';
import { QueryParamName } from 'clerk_common/constants/urls/queryParams';
import { useCallback, useContext, useRef } from 'react';

export type AnalyticsActions = {
  track: (event: string, properties?: TrackProperties) => void;
  page: (name: string, properties?: PageProperties) => void;
};

export type AnalyticsState = {
  loadingTrack: boolean;
  loadingPage: boolean;
};

const getNavigatorFields = (): NavigatorFields | undefined => {
  if (typeof window === 'undefined') return;

  return {
    appName: window.navigator?.appName,
    appVersion: window.navigator?.appVersion,
    language: window.navigator?.language,
    platform: window.navigator?.platform,
    userAgent: window.navigator?.userAgent,
    vendor: window.navigator?.vendor,
  };
};

type AnalyticsMetadata = {
  apiClient: VoomaApiClient;
  clientVersion?: string;
  defaultOrgId?: string | null;
  defaultOrgName?: string | null;
};

const buildContext = ({ apiClient, clientVersion }: AnalyticsMetadata) => ({
  apiClient,
  clientVersion,
  navigator: getNavigatorFields(),
});

const buildOrganizationProperties = ({
  defaultOrgId,
  defaultOrgName,
}: AnalyticsMetadata) => ({
  organizationId: defaultOrgId ?? undefined,
  organizationName: defaultOrgName ?? undefined,
});

// NOTE(parlato): Callers list `track` in effect deps, so it stays stable while
// org and client-version metadata load in; handlers read them from a ref.
export const useAnalytics = (): AnalyticsActions & AnalyticsState => {
  const { defaultOrgName, defaultOrgId } = useMeContext();
  const { apiClient } = useContext(AnalyticsContext);
  const [track, { loading: loadingTrack }] = useTrackMutation();
  const [page, { loading: loadingPage }] = usePageMutation();
  const clientVersion = useCurrentClientVersion();
  const metadata = { apiClient, clientVersion, defaultOrgId, defaultOrgName };
  const metadataRef = useRef(metadata);
  metadataRef.current = metadata;

  const handleTrack = useCallback(
    (event: string, properties?: TrackProperties) =>
      track({
        variables: {
          input: {
            event,
            properties: {
              ...buildOrganizationProperties(metadataRef.current),
              ...properties,
            },
            context: buildContext(metadataRef.current),
          },
        },
      }),
    [track]
  );

  const handlePage = useCallback(
    (name: string, properties?: PageProperties) => {
      const location = window.location;
      const params = new URLSearchParams(location.search);
      const source = params.get(QueryParamName.UTM_SOURCE) ?? undefined;
      page({
        variables: {
          input: {
            name,
            properties: {
              url: location.pathname + location.search,
              ...buildOrganizationProperties(metadataRef.current),
              source,
              ...properties,
            },
            context: buildContext(metadataRef.current),
          },
        },
      });
    },
    [page]
  );

  return {
    page: handlePage,
    track: handleTrack,
    loadingPage,
    loadingTrack,
  };
};
